import React, { useState, useEffect } from "react";
import { StyleSheet, View, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  isAudioMedia,
  isVideoMedia,
  isPdfMedia,
  isDocumentMedia,
} from "common247";
import { ThemedText } from "../ThemedText";
import { VideoPlayer } from "./VideoPlayer";
import { LazyMediaImage } from "./LazyMediaImage";
import { ThemedAudioPlayer } from "./ThemedAudioPlayer";

export interface IMediaPreviewProps {
  url: string;
  fallbackUrl?: string;
  contentType?: string;
  fileName?: string;
  name?: string;
  style: any;
  resizeMode?: "cover" | "contain";
  allowInlinePlayer?: boolean;
  isUnavailable?: boolean;
}

/**
 * Renders media preview across images, audio, video, PDF, and document types,
 * including external media unavailability and fallback URL recovery.
 */
export function MediaPreview({
  url,
  fallbackUrl,
  contentType,
  fileName,
  name,
  style,
  resizeMode = "cover",
  allowInlinePlayer = false,
  isUnavailable = false,
}: IMediaPreviewProps) {
  const [activeUrl, setActiveUrl] = useState<string>(() => (url ? url.trim() : ""));
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setActiveUrl(url ? url.trim() : "");
    setHasError(false);
  }, [url]);

  const mediaName = name || fileName;
  const isAudio = isAudioMedia(contentType, mediaName, activeUrl || url);
  const isVideo = isVideoMedia(contentType, mediaName, activeUrl || url);
  const isPdf = isPdfMedia(contentType, mediaName, activeUrl || url);
  const isDoc = isDocumentMedia(contentType, mediaName, activeUrl || url);

  const flattenedStyle = StyleSheet.flatten(style) || {};
  const isCompactOrThumb =
    (flattenedStyle.height != null && typeof flattenedStyle.height === "number" && flattenedStyle.height <= 40) ||
    (flattenedStyle.width != null && typeof flattenedStyle.width === "number" && flattenedStyle.width <= 40);

  if (isUnavailable) {
    return (
      <View
        testID="media-unavailable-preview"
        style={[
          style,
          styles.mediaPlaceholder,
          {
            backgroundColor: "rgba(220, 53, 69, 0.08)",
            borderWidth: 1,
            borderColor: "rgba(220, 53, 69, 0.3)",
          },
        ]}
      >
        <Ionicons name="cloud-offline-outline" size={isCompactOrThumb ? 16 : 32} color="#DC3545" />
        {!isCompactOrThumb && (
          <ThemedText style={[styles.mediaPlaceholderText, { color: "#DC3545", fontWeight: "700" }]}>
            External Media Unavailable
          </ThemedText>
        )}
      </View>
    );
  }

  if (isAudio) {
    if (resizeMode === "contain" && allowInlinePlayer) {
      return (
        <View style={[style, styles.mediaPlayerContainer]}>
          <Ionicons name="musical-notes" size={48} color="#4A90E2" style={{ marginBottom: 12 }} />
          <ThemedAudioPlayer
            url={activeUrl || url}
            title={mediaName}
            style={{ width: "100%", maxWidth: 400 }}
          />
        </View>
      );
    }
    return (
      <View style={[style, styles.mediaPlaceholder, { backgroundColor: "rgba(74, 144, 226, 0.1)" }]}>
        <Ionicons name="musical-notes" size={32} color="#4A90E2" />
        <ThemedText style={styles.mediaPlaceholderText}>Audio Clip</ThemedText>
      </View>
    );
  }

  if (isVideo) {
    const videoUrl = (activeUrl || url || "").trim();
    if (allowInlinePlayer && videoUrl) {
      return (
        <VideoPlayer
          url={videoUrl}
          contentType={contentType}
          name={mediaName}
          style={style}
          resizeMode="contain"
        />
      );
    }
    return (
      <View style={[style, styles.videoPreviewContainer]}>
        <View style={[StyleSheet.absoluteFill, styles.mediaPlaceholder, { backgroundColor: "rgba(234, 67, 53, 0.08)" }]}>
          <Ionicons name="videocam" size={32} color="#EA4335" />
          <ThemedText style={styles.mediaPlaceholderText}>Video Clip</ThemedText>
        </View>
        <View style={styles.videoPlayBadge}>
          <Ionicons name="play" size={16} color="#FFFFFF" />
        </View>
      </View>
    );
  }

  if (isPdf) {
    return (
      <View
        testID="pdf-media-preview"
        style={[
          style,
          styles.mediaPlaceholder,
          {
            backgroundColor: "rgba(220, 53, 69, 0.08)",
            borderWidth: 1,
            borderColor: "rgba(220, 53, 69, 0.2)",
          },
        ]}
      >
        <Ionicons name="document-text" size={isCompactOrThumb ? 16 : 36} color="#DC3545" />
        {!isCompactOrThumb && (
          <ThemedText style={[styles.mediaPlaceholderText, { color: "#DC3545", fontWeight: "600" }]}>
            PDF Document
          </ThemedText>
        )}
      </View>
    );
  }

  if (isDoc) {
    return (
      <View
        testID="document-media-preview"
        style={[
          style,
          styles.mediaPlaceholder,
          {
            backgroundColor: "rgba(74, 144, 226, 0.08)",
            borderWidth: 1,
            borderColor: "rgba(74, 144, 226, 0.2)",
          },
        ]}
      >
        <Ionicons name="document-text-outline" size={isCompactOrThumb ? 16 : 36} color="#4A90E2" />
        {!isCompactOrThumb && (
          <ThemedText style={[styles.mediaPlaceholderText, { color: "#4A90E2", fontWeight: "600" }]}>
            Document
          </ThemedText>
        )}
      </View>
    );
  }

  const cleanUrl = activeUrl ? activeUrl.trim() : "";
  const sanitizedUrl = cleanUrl.includes(" ") ? encodeURI(cleanUrl) : cleanUrl;

  const handleError = () => {
    if (fallbackUrl && fallbackUrl.trim() && activeUrl !== fallbackUrl.trim()) {
      setActiveUrl(fallbackUrl.trim());
    } else {
      setHasError(true);
    }
  };
  if (hasError || !sanitizedUrl) {
    return (
      <View style={[style, styles.mediaPlaceholder, { backgroundColor: "rgba(150, 150, 150, 0.08)" }]}>
        <Ionicons name="image-outline" size={isCompactOrThumb ? 16 : 36} color="#999" />
        {!isCompactOrThumb && (
          <ThemedText style={styles.mediaPlaceholderText}>Image unavailable</ThemedText>
        )}
      </View>
    );
  }

  return (
    <LazyMediaImage
      url={sanitizedUrl}
      fallbackUrl={fallbackUrl}
      name={mediaName}
      style={style}
      resizeMode={resizeMode === "contain" ? "contain" : "cover"}
      isCompactOrThumb={isCompactOrThumb}
      onError={handleError}
    />
  );
}

const styles = StyleSheet.create({
  mediaPlaceholder: {
    justifyContent: "center",
    alignItems: "center",
    gap: 4,
  },
  mediaPlaceholderText: {
    fontSize: 10,
    fontWeight: "bold",
    opacity: 0.6,
  },
  videoPreviewContainer: {
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
  },
  videoPlayBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1.5,
    borderColor: "rgba(255, 255, 255, 0.85)",
  },
  mediaPlayerContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(74, 144, 226, 0.05)",
    padding: 10,
    borderRadius: 0,
  },
});
